import { useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  ImageUploadError,
  uploadOrderAttachment,
} from '../lib/image-upload';
import { haptic } from '../lib/telegram';
import type { OrderAttachment } from '../types';
import { DocumentTile, isDocumentAttachment } from './order-files';
import { ZoomableImage } from './zoomable-image';

/** Столько же, сколько принимает `attachmentIds` при смене этапа. */
const ATTACHMENTS_LIMIT = 10;

/**
 * Файлы к смене этапа. Загружаются сразу, а к переходу привязываются его
 * идентификаторами: иначе картинку пришлось бы держать в памяти формы и слать
 * одним запросом вместе со сменой этапа — на телефоне это самый ненадёжный
 * момент. Загруженный файл виден в карточке заказа, даже если окно закрыли.
 */
export function AttachmentPicker({
  orderId,
  visibility,
  attachments,
  onChange,
}: {
  orderId: string;
  /** Приватное вложение остаётся у художника: заказчик его не увидит. */
  visibility: 'PUBLIC' | 'PRIVATE';
  attachments: OrderAttachment[];
  onChange: (attachments: OrderAttachment[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const full = attachments.length >= ATTACHMENTS_LIMIT;

  const pick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setPending(true);
    setError(null);
    const uploaded: OrderAttachment[] = [];

    try {
      // Последовательно: параллельная загрузка нескольких снимков на мобильной
      // сети чаще заканчивается таймаутом, чем выигрывает время.
      for (const file of [...files].slice(0, ATTACHMENTS_LIMIT - attachments.length)) {
        uploaded.push(await uploadOrderAttachment(orderId, file, visibility));
      }
      haptic('success');
    } catch (failure) {
      setError(
        failure instanceof ImageUploadError || failure instanceof ApiError
          ? failure.message
          : 'Не удалось загрузить файл',
      );
    } finally {
      if (uploaded.length > 0) onChange([...attachments, ...uploaded]);
      setPending(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="attachment-picker">
      <div className="image-field__head">
        <span className="field__label">Файлы</span>
        <span className="field__hint">
          {attachments.length}/{ATTACHMENTS_LIMIT}
        </span>
      </div>

      {attachments.length > 0 && (
        <div className="attachment-picker__list">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-picker__item">
              {attachment.url && !isDocumentAttachment(attachment.mimeType) ? (
                <ZoomableImage url={attachment.url} alt={attachment.fileName} />
              ) : (
                <DocumentTile fileName={attachment.fileName} sizeBytes={attachment.sizeBytes} />
              )}
              <button
                type="button"
                aria-label="Убрать файл"
                onClick={() => onChange(attachments.filter((item) => item.id !== attachment.id))}
              >
                Убрать
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="image-field__frame image-field__frame--wide"
        disabled={pending || full}
        onClick={() => input.current?.click()}
      >
        <span>{pending ? 'Загружаем…' : full ? 'Больше нельзя' : '+ Прикрепить'}</span>
      </button>
      {error ? (
        <span className="field__error">{error}</span>
      ) : (
        <span className="field__hint">
          {visibility === 'PRIVATE'
            ? 'Видно только вам'
            : 'Заказчик увидит их вместе с этапом — покажите, как идёт работа'}
        </span>
      )}

      <input
        ref={input}
        type="file"
        accept={ACCEPTED_ATTACHMENT_TYPES}
        multiple
        hidden
        onChange={(event) => void pick(event.target.files)}
      />
    </div>
  );
}
